import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { loginApi } from "apis/eduidLogin";
import { EduIDButton } from "components/Common/EduIDButton";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { LOGIN_BASE_PATH } from "helperFunctions/paths";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { clearNotifications } from "slices/Notifications";
import resetPasswordSlice from "slices/ResetPassword";
import loginSlice from "../../slices/Login";
import { AssuranceInfo } from "./AssuranceInfo";
import { MultiFactorAuth } from "./MultiFactorAuth";
import { NewDevice, initKnownDevice } from "./NewDevice";
import { SubmitSamlResponse } from "./SubmitSamlResponse";
import { TermsOfUse } from "./TermsOfUse";
import { UseOtherDevice1 } from "./UseOtherDevice1";
import { UseOtherDevice2 } from "./UseOtherDevice2";
import { UsernamePw } from "./UsernamePw";
/* keep all use through functions in this module */
export const HAS_READ_ANNOUNCEMENT = "hasReadAnnouncement";
export function Login() {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useAppDispatch();
    const base_url = useAppSelector((state) => state.config.login_service_url);
    const next_page = useAppSelector((state) => state.login.next_page);
    const fetching_next = useAppSelector((state) => state.login.fetching_next);
    const this_device = useAppSelector((state) => state.login.this_device);
    const remember_me = useAppSelector((state) => state.login.remember_me);
    const ref = useAppSelector((state) => state.login.ref);
    const error_state = useAppSelector((state) => state.login.error);
    const [fetchNext, { isError }] = loginApi.useLazyFetchNextQuery();
    const redirectingToSignup = useRef(false);
    useEffect(() => {
        if (isError) {
            return;
        }
        // if this_device and remember_me haven't been set in redux state yet, initialise them from local storage
        const { init_this_device, init_remember_me } = initKnownDevice(this_device, remember_me, dispatch);
        let init_ref = ref;
        if (ref === undefined && params.ref !== undefined) {
            const url_ref = params.ref;
            init_ref = url_ref;
            dispatch(loginSlice.actions.addLoginRef({ ref: url_ref, start_url: globalThis.location.href }));
        }
        // Ask the backend what to do
        if (base_url && !next_page && init_ref && !fetching_next && init_remember_me !== undefined && !error_state) {
            fetchNext({ ref: init_ref, this_device: init_this_device, remember_me: init_remember_me });
        }
    }, [
        base_url,
        ref,
        this_device,
        remember_me,
        next_page,
        params.ref,
        dispatch,
        fetching_next,
        error_state,
        fetchNext,
        isError,
        navigate,
    ]);
    useEffect(() => {
        if (redirectingToSignup.current) {
            return;
        }
        if (ref !== undefined) {
            /* Changing URL is apparently what triggers browsers password managers, so we
             * change to/from 'login/password' when that module is used.
             */
            if (next_page === "USERNAMEPASSWORD") {
                navigate(`${LOGIN_BASE_PATH}/password/${ref}`);
            }
            else if (next_page === "PASSWORD") {
                navigate(`${LOGIN_BASE_PATH}/mfa/password/${ref}`);
            }
            else {
                navigate(`${LOGIN_BASE_PATH}/${ref}`);
            }
        }
    }, [navigate, next_page, ref]);
    return (_jsxs(_Fragment, { children: [next_page === "NEW_DEVICE" && _jsx(NewDevice, {}), next_page === "OTHER_DEVICE" && _jsx(UseOtherDevice1, {}), next_page === "USERNAMEPASSWORD" && _jsx(UsernamePw, {}), next_page === "TOU" && _jsx(TermsOfUse, {}), next_page === "MFA" && _jsx(MultiFactorAuth, {}), next_page === "PASSWORD" && _jsx(UsernamePw, {}), next_page === "FINISHED" && _jsx(RenderFinished, {}), error_state === "login.user_terminated" && _jsx(UserTerminated, {})] }));
}
function RenderFinished() {
    const SAMLParameters = useAppSelector((state) => state.login.saml_parameters);
    const assurance = useAppSelector((state) => state.login.assurance);
    const [proceedAnyway, setProceedAnyway] = useState(false);
    if (!SAMLParameters) {
        return _jsx(UseOtherDevice2, {});
    }
    // The SP requires a higher assurance level than the IdP is about to assert.
    // Inform the user before posting the SAML response (the SP makes the final decision).
    if (assurance && !assurance.fulfilled && !proceedAnyway) {
        return _jsx(AssuranceInfo, { onContinue: () => setProceedAnyway(true) });
    }
    return _jsx(SubmitSamlResponse, {});
}
function UserTerminated() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const error_state = useAppSelector((state) => state.login.error);
    const [fetchLogout] = loginApi.useLazyFetchLogoutQuery();
    useEffect(() => {
        // If we have an error and localStorage is not empty, we need to logout the user from the session
        if (error_state && globalThis.localStorage.length > 0) {
            // make sure the backend idp logs out the user from the session to get out of a stuck state
            fetchLogout({});
            // clear localStorage so that the same user is not used again
            globalThis.localStorage.clear();
            dispatch(resetPasswordSlice.actions.resetState());
        }
    }, [error_state, fetchLogout, dispatch]);
    function reset_password() {
        dispatch(clearNotifications());
        navigate("/reset-password");
    }
    return (_jsxs("section", { children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "login.heading", defaultMessage: "Account terminated", description: "Account terminated - heading" }) }), _jsx("p", { children: _jsx(FormattedMessage, { id: "login.paragraph", defaultMessage: "This account has recently been terminated and can not be used to log in. It is possible to re-activate the account shortly afterwards by resetting the password using the link below.", description: "Account terminated - paragraph" }) }), _jsx("div", { children: _jsx(EduIDButton, { onClick: reset_password, buttonstyle: "link normal-case", children: _jsx(FormattedMessage, { id: "login.link", defaultMessage: "Go to reset password page", description: "Account terminated - reset password link" }) }) }), _jsx("p", { className: "text-small", children: _jsx(FormattedMessage, { id: "login.account", defaultMessage: "To log in with another account go to the start page by clicking the eduID logo in the header, or create a new account using the Create eduiD button.", description: "Account terminated - help text" }) })] }));
}
